// @flow
import * as React from 'react';
import {defineMessage} from 'react-intl';
import session from '../session';
import {
  DefaultCards,
  PaymentMethodPicker,
  DefaultPricePicker,
  RunServerCommandShell,
} from '../../../components/left-col';

import type {StepConfig, StepsConfig} from '../../../data/GuideTypes';

function code(str) {
  return <code>{str}</code>;
}

export const automaticTaxStep: StepConfig = {
  extension: 'extra',
  id: 'automatic-tax',
  filter: (opt) => opt?.taxActivationStatus !== 'unavailable',
  heading: {
    message: defineMessage({
      id: 'docsShell.ib.checkout.html.automatic-tax.heading',
      defaultMessage: 'Automate tax collection',
    }),
  },
  body: {
    message: defineMessage({
      id: 'docsShell.ib.checkout.html.automatic-tax.body',
      defaultMessage:
        'Calculate and collect the right amount of tax on your Stripe transactions. Learn more about <a1>Stripe Tax</a1> and <a2>how to add it to Checkout</a2>.',
    }),
    values: {
      a1: (str) => (
        <a href="/docs/tax" target="_blank">
          {str}
        </a>
      ),
      a2: (str) => (
        <a href="/docs/tax/checkout" target="_blank">
          {str}
        </a>
      ),
    },
  },
  steps: [
    {
      id: 'automatic-tax-setup',
      testing: true,
      heading: {
        message: defineMessage({
          id: 'docsShell.ib.checkout.html.automatic-tax-setup.heading',
          defaultMessage: 'Set up Stripe Tax in the dashboard',
        }),
      },
      filter: (opt) => !(opt?.taxActivationStatus === 'active'),
      body: {
        message: defineMessage({
          id: 'docsShell.ib.checkout.html.automatic-tax-setup.body',
          defaultMessage:
            '<a>Activate Stripe Tax</a> to monitor your tax obligations, automatically collect tax, and access the reports you need to file returns.',
        }),
        values: {
          a: (str) => (
            <a
              href="https://dashboard.stripe.com/settings/tax/activate"
              target="_blank"
            >
              {str}
            </a>
          ),
        },
      },
    },
    {
      id: 'automatic-tax-param',
      heading: {
        message: defineMessage({
          id: 'docsShell.ib.checkout.html.automatic-tax-param.heading',
          defaultMessage: 'Add the automatic tax parameter',
        }),
      },
      body: {
        message: defineMessage({
          id: 'docsShell.ib.checkout.html.automatic-tax-param.body',
          defaultMessage:
            'Set the <code>automatic_tax</code> parameter to <code>enabled: true</code>.',
        }),
        values: {
          code,
        },
      },
    },
  ],
};

export const paymentMethodsStep: StepConfig = {
  file: 'server',
  id: 'payment-methods',
  section: 'setup-stripe',
  heading: {
    message: defineMessage({
      id: 'docsApp.ib.checkout.next.js.payment-methods.heading',
      defaultMessage: 'Specify at least one payment method',
    }),
  },
  body: {
    message: defineMessage({
      id: 'docsApp.ib.checkout.next.js.payment-methods.body',
      defaultMessage: `When you pass multiple <a>payment methods</a>, Checkout dynamically displays them to prioritize what's most relevant to the customer. <a2>Apple Pay</a2> and <a3>Google Pay</a3> are included automatically when you include <code>card</code> in <code>payment_method_types</code>.`,
    }),
    values: {
      a: (str) => (
        <a href="/docs/payments/checkout/payment-methods" target="_blank">
          {str}
        </a>
      ),
      a2: (str) => (
        <a href="/docs/apple-pay" target="_blank">
          {str}
        </a>
      ),
      a3: (str) => (
        <a href="/docs/google-pay" target="_blank">
          {str}
        </a>
      ),
      code,
    },
  },
  Component: PaymentMethodPicker,
};

export const lineItemsStep: StepConfig = {
  file: 'server',
  id: 'line-items',
  section: 'setup-stripe',
  heading: {
    message: defineMessage({
      id: 'docsShell.ib.checkout.html.line-items.heading',
      defaultMessage: 'Define a product to sell',
    }),
  },
  body: {
    message: defineMessage({
      id: 'docsShell.ib.checkout.html.line-items.body',
      defaultMessage: `Always keep sensitive information about your product inventory, like price and availability, on your server to prevent customer manipulation from the client. Define product information when you create the Checkout Session using <a>predefined price IDs</a> or on the fly with <code><a2>price_data</a2></code>.`,
    }),
    values: {
      a: (str) => (
        <a
          href="/docs/payments/accept-a-payment?platform=web&ui=checkout#create-product-prices-upfront"
          target="_blank"
        >
          {str}
        </a>
      ),
      a2: (str) => (
        <a
          href="/docs/api/checkout/sessions/create#create_checkout_session-line_items-price_data"
          target="_blank"
        >
          {str}
        </a>
      ),
      code,
    },
  },
  Component: DefaultPricePicker,
};

export default ([
  {
    file: 'server',
    id: 'setup',
    section: 'setup-stripe',
  },
  {
    file: 'server',
    id: 'create',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.create.heading',
        defaultMessage: 'Create a Checkout Session',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.create.body',
        defaultMessage: `Add an endpoint on your server that creates a <a>Checkout Session</a>. A Checkout Session controls what your customer sees in the Stripe-hosted payment page such as line items, the order amount and currency, and acceptable payment methods.`,
      }),
      values: {
        a: (str) => (
          <a href="/docs/api/checkout/sessions" target="_blank">
            {str}
          </a>
        ),
      },
    },
    response: session.html,
  },
  lineItemsStep,
  paymentMethodsStep,
  {
    file: 'server',
    id: 'mode',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.mode.heading',
        defaultMessage: 'Choose the mode',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.mode.body',
        defaultMessage: `Checkout has three modes: <code>payment</code>, <code>subscription</code>, or <code>setup</code>. Use <code>payment</code> mode for one-time purchases. Learn more about <subscriptionA>subscription</subscriptionA> and <setupA>setup</setupA> modes in the docs.`,
      }),
      values: {
        subscriptionA: (str) => (
          <a
            href="/docs/payments/checkout/set-up-a-subscription"
            target="_blank"
          >
            {str}
          </a>
        ),
        setupA: (str) => (
          <a href="/docs/payments/save-and-reuse#checkout" target="_blank">
            {str}
          </a>
        ),
        code: (str) => <code>{str}</code>,
      },
    },
  },
  {
    file: 'server',
    id: 'urls',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.urls.heading',
        defaultMessage: 'Supply success and cancel URLs',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.urls.body',
        defaultMessage: `Specify URLs for success and cancel pages—make sure they are publicly accessible so Stripe can redirect customers to them. You can also handle both the success and canceled states with the same URL.`,
      }),
    },
  },
  {
    file: 'server',
    id: 'redirect',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.redirect.heading',
        defaultMessage: 'Redirect to Checkout',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.redirect.body',
        defaultMessage: `After creating the session, redirect your customer to the Checkout page's URL returned in the response.`,
      }),
    },
  },
  {
    id: 'success-page',
    section: 'build-page',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.success-page.heading',
        defaultMessage: 'Add a success page',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.success-page.body',
        defaultMessage:
          'Create a success page for the URL you provided as the Checkout Session <code>success_url</code> to display order confirmation messaging or order details to your customer. Stripe redirects to this page after the customer successfully completes the checkout.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
      },
    },
  },
  {
    id: 'cancel-page',
    section: 'build-page',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.cancel-page.heading',
        defaultMessage: 'Add a canceled page',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.cancel-page.body',
        defaultMessage:
          'Add another page for <code>cancel_url</code>. Stripe redirects to this page when the customer clicks the back button in Checkout.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
      },
    },
  },
  {
    id: 'preview-page',
    section: 'build-page',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.preview-page.heading',
        defaultMessage: 'Add an order preview page',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.preview-page.body',
        defaultMessage:
          "Finally, add a page to show a preview of the customer's order. Allow the customer to review or modify their order—once a customer is sent to the Checkout page, the order is final and cannot be modified without creating a new Checkout Session.",
      }),
    },
  },
  {
    id: 'add-button',
    section: 'build-page',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.add-button.heading',
        defaultMessage: 'Add a checkout button',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.add-button.body',
        defaultMessage:
          "Add a button to your order preview page. When your customer clicks this button, they're redirected to the Stripe-hosted payment page.",
      }),
    },
  },
  {
    id: 'run-server',
    section: 'testing',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.run-server.heading',
        defaultMessage: 'Run the application',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.run-server.body',
        defaultMessage:
          'Start your server and navigate to <a>http://localhost:4242/checkout.html</a>',
      }),
      values: {
        a: (str) => <a href="http://localhost:4242/checkout.html">{str}</a>,
      },
    },
    Component: RunServerCommandShell,
  },
  {
    id: 'testing',
    section: 'testing',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.testing.heading',
        defaultMessage: 'Try it out',
      }),
    },
    testing: true,
    body: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.testing.body',
        defaultMessage:
          'Click the checkout button to be redirected to the Stripe Checkout page. Use any of these test cards to simulate a payment.',
      }),
    },
    Component: DefaultCards,
  },
  {
    extension: 'extra',
    id: 'customize',
    heading: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.customize.heading',
        defaultMessage: 'Customize the checkout page',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.ib.checkout.html.customize.body',
        defaultMessage:
          "You can customize the appearance of the hosted Checkout page by adding your logo and color theme in the Dashboard's <brandingA>branding settings</brandingA>, or by using the API to activate additional features like address collection. Explore a few examples below, or see the <apiRefA>API reference</apiRefA> for the full list of configurable fields.",
      }),
      values: {
        brandingA: (str) => (
          <a
            href="https://dashboard.stripe.com/settings/branding"
            target="_blank"
          >
            {str}
          </a>
        ),
        apiRefA: (str) => (
          <a
            href="https://stripe.com/docs/api/checkout/sessions/create"
            target="_blank"
          >
            {str}
          </a>
        ),
      },
    },
    steps: [
      {
        id: 'prefill-email',
        heading: {
          message: defineMessage({
            id: 'docsShell.ib.checkout.html.prefill-email.heading',
            defaultMessage: 'Prefill customer data',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsShell.ib.checkout.html.prefill-email.body',
            defaultMessage:
              "Use <code>customer_email</code> to prefill the customer's email address in the email input field. You can also pass a <a>Customer</a> ID to <code>customer</code> field to prefill the email address field with the email stored on the Customer.",
          }),
          values: {
            a: (str) => (
              <a href="/docs/api/customers" target="_blank">
                {str}
              </a>
            ),
            code: (str) => <code>{str}</code>,
          },
        },
      },
      {
        id: 'submit-button',
        heading: {
          message: defineMessage({
            id: 'docsShell.ib.checkout.html.submit-button.heading',
            defaultMessage: 'Pick a submit button',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsShell.ib.checkout.html.submit-button.body',
            defaultMessage:
              'Configure the copy displayed on the Checkout submit button by setting the <code>submit_type</code>. There are four different submit types.',
          }),
          values: {
            code: (str) => <code>{str}</code>,
          },
        },
      },
      {
        id: 'address',
        heading: {
          message: defineMessage({
            id: 'docsShell.ib.checkout.html.address.heading',
            defaultMessage: 'Collect billing and shipping details',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsShell.ib.checkout.html.address.body',
            defaultMessage:
              "Use <code>billing_address_collection</code> and <code>shipping_address_collection</code> to collect your customer's address. <code>shipping_address_collection</code> requires a list of <code>allowed_countries</code>. Checkout displays the list of allowed countries in a dropdown on the page.",
          }),
          values: {
            code: (str) => <code>{str}</code>,
          },
        },
      },
      {
        id: 'testing-customized',
        section: 'testing',
        heading: {
          message: defineMessage({
            id: 'docsShell.ib.checkout.html.testing-customized.heading',
            defaultMessage: 'Preview the customized page',
          }),
        },
        testing: true,
        body: {
          message: defineMessage({
            id: 'docsShell.ib.checkout.html.testing-customized.body',
            defaultMessage:
              'Click the checkout button in the preview on the right to see a sample Stripe Checkout page with these additional fields. <a>Read our docs</a> for all the ways you can customize Checkout—we continuously add new features.',
          }),
          values: {
            a: (str) => (
              <a href="/docs/payments/checkout/customization" target="_blank">
                {str}
              </a>
            ),
          },
        },
      },
    ],
  },
  automaticTaxStep,
]: StepsConfig);
